import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesCardHeader,
  AgnesCardTitle,
  AgnesDataTable,
  AgnesInput,
  AgnesLabel,
  AgnesPageHeader,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
} from '@agnes/ui';
import { ArrowLeft, CalendarClock, Download, Save } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { useJornadaDetalheStore } from '@/stores/useJornadaDetalheStore';
import {
  camposObrigatoriosAnalisado,
  derivarClassificacao,
  type FormRecomendacao,
  toUpdatePayload,
} from '@/lib/jornadaDetalhe';
import { GENERO_LABEL, PCD_LABEL, SITUACAO_CONTRATUAL_LABEL, TIPO_ADMISSAO_LABEL } from '@/lib/cbo';
import { CLASSIFICATION_LABEL, RISK_LABEL, STEP_LABEL } from '@/lib/jornada';
import { isPerfilAnalista } from '@/utils/perfil';
import { useDirty } from '@/hooks/useDirty';
import { confirm } from '@/stores/useConfirm';
import { notify } from '@/stores/useToast';
import ServiceHorarios from '@/services/ServiceHorarios';
import UtilsDate from '@/utils/UtilsDate';
import UtilsMask from '@/utils/UtilsMask';
import type { HistoricoMovimentacao } from '@/stores/useHorariosStore';
import { comQaNasCelulas } from '@/lib/dataQa';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const NOTURNO_OPTS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

function Campo({
  label,
  children,
  dataQa,
}: {
  label: string;
  children: React.ReactNode;
  dataQa: string;
}) {
  return (
    <div data-qa={`${dataQa}-campo`} className="flex flex-col gap-1">
      <AgnesLabel dataQa={`${dataQa}-rotulo`}>{label}</AgnesLabel>
      {children}
    </div>
  );
}

// CON-4899: ordem das colunas da aba Movimentações (Recepção no eSocial primeiro,
// depois Ocorrência do Evento, Evento, ...). Exportado para teste de ordenação.
export const MOVIMENTACOES_COLUMNS = [
  { key: 'recepcao', header: 'Recepção no eSocial', width: 180 },
  { key: 'ocorrenciaEvento', header: 'Ocorrência do Evento', width: 180 },
  { key: 'eventoDesc', header: 'Evento', width: 320 },
  { key: 'identificacao', header: 'Identificação do Evento', width: 300 },
  { key: 'declaracao', header: 'Declaração', width: 140 },
  { key: 'recibo', header: 'Número do Recibo', width: 220 },
  { key: 'reciboRetificado', header: 'Número do Recibo Retificado', width: 220 },
  { key: 'versao', header: 'Versão eSocial', width: 130 },
  { key: 'importacao', header: 'Importação em Agnes', width: 180 },
];

export default function PageTrabalhadorHorariosDetalhe() {
  const { analiseId } = useParams();
  const id = Number(analiseId);
  const navigate = useNavigate();
  const analista = React.useMemo(() => isPerfilAnalista(), []);
  const {
    detalhe,
    optWorkRegime,
    optJourneyType,
    optPartTime,
    ocorrencias,
    salvando,
    carregarDetalhe,
    carregarOptions,
    carregarOcorrencias,
    salvar,
    eventos,
    eventosTotal,
    eventosFiltros,
    eventosPage,
    carregarEventos,
    carregarFiltrosEventos,
    exportarEventos,
    setEventosPage,
    setEventosFiltro,
  } = useJornadaDetalheStore();

  const [form, setForm] = React.useState<FormRecomendacao | null>(null);
  const [historico, setHistorico] = React.useState<HistoricoMovimentacao[]>([]);
  const [risco, setRisco] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState('analise');

  React.useEffect(() => {
    carregarDetalhe(id);
    carregarOptions();
    carregarOcorrencias();
    ServiceHorarios.movimentacoes(id, {}, { page: 0, size: 100 })
      .then((r) => setHistorico(r.data?.content ?? []))
      .catch(() => {});
  }, [id, carregarDetalhe, carregarOptions, carregarOcorrencias]);

  React.useEffect(() => {
    if (!detalhe) return;
    setForm({
      step: (detalhe.step as string) ?? 'ANALISAR',
      recommendationId: (detalhe.recommendationId as number) ?? null,
      comentarios: (detalhe.comentariosRecomendacoesAdicionais as string) ?? '',
      weeklyWorkHours: (detalhe.recCargaHorariaSemanal as number) ?? null,
      workScheduleTypeCode: (detalhe.recTipoCode as number) ?? null,
      partTimeContractCode: (detalhe.recTempoCode as number) ?? null,
      nightShift: (detalhe.recNightShift as boolean) ?? null,
      workScheduleRegTypeCode: (detalhe.recRegimeCode as number) ?? null,
    });
    setRisco((detalhe.risco as string) ?? null);
    if (detalhe.idTrabalhador != null) {
      const idTrab = detalhe.idTrabalhador as number;
      carregarEventos(idTrab);
      carregarFiltrosEventos(idTrab);
    }
  }, [detalhe, carregarEventos, carregarFiltrosEventos]);

  // CON-4980: Salvar desabilitado até haver alteração; rebase() após salvar.
  const { isDirty, rebase } = useDirty(form);

  if (!form || !detalhe)
    return (
      <div data-qa="horarios-detalhe-carregando" className="p-6">
        Carregando…
      </div>
    );

  const idTrab = detalhe.idTrabalhador as number;
  const nomeTrabalhador = [detalhe.nome, detalhe.nomeSocial].filter(Boolean).join(' / ');
  const situacao = (detalhe.situacao as string | null) ?? '';
  const situacaoContratual = SITUACAO_CONTRATUAL_LABEL[situacao.toUpperCase()] ?? situacao;
  const pcd = detalhe.pcd == null ? '' : (PCD_LABEL[String(detalhe.pcd)] ?? String(detalhe.pcd));
  const tipoAdmissao = (detalhe.tipoAdmissao as string | null) ?? '';
  const genero = (detalhe.genero as string | null) ?? '';

  const set = <K extends keyof FormRecomendacao>(k: K, v: FormRecomendacao[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const onOcorrencia = (v: string) => {
    const rid = v ? Number(v) : null;
    set('recommendationId', rid);
    const oc = ocorrencias.find((o) => o.id === rid);
    setRisco(oc?.risk ?? null);
  };

  const classificacaoPreview = derivarClassificacao(form.step, risco);

  const onSalvar = async () => {
    const faltas = camposObrigatoriosAnalisado(form);
    if (faltas.length) {
      notify.error(`Preencha para concluir a análise: ${faltas.join(', ')}`);
      return;
    }
    await salvar(id, toUpdatePayload(form));
    rebase();
  };

  const onVoltar = async () => {
    if (
      isDirty &&
      !(await confirm({
        title: 'Alterações não salvas',
        message: 'Você tem alterações que não foram salvas e serão perdidas. Deseja sair?',
        okLabel: 'Sair',
        cancelLabel: 'Continuar editando',
      }))
    ) {
      return;
    }
    navigate('/esocial/trabalhador/horarios');
  };

  const histCols = [
    {
      key: 'changedFieldDescription',
      header: 'Campo',
      width: 220,
      render: (r: HistoricoMovimentacao) => r.changedFieldDescription ?? r.changedField ?? '-',
    },
    { key: 'fromValue', header: 'De', width: 200 },
    { key: 'toValue', header: 'Para', width: 200 },
    {
      key: 'updatedBy',
      header: 'Alterado por',
      width: 160,
      render: (r: HistoricoMovimentacao) => r.updatedByName ?? r.updatedBy ?? '-',
    },
    { key: 'updatedAt', header: 'Data', width: 160 },
  ];

  return (
    <>
      <AgnesPageHeader
        dataQa="horarios-detalhe-cabecalho"
        icon={<CalendarClock />}
        title={`${detalhe.nome ?? ''}`}
        subtitle={`${UtilsMask.cpf(detalhe.cpf as string)} · ${detalhe.empresa ?? ''}`}
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Horários e jornadas', href: '/esocial/trabalhador/horarios' },
          { label: (detalhe.nome as string) ?? 'Detalhe' },
        ]}
        actions={
          <AgnesButton
            variant="outlined"
            data-qa="horarios-detalhe-voltar"
            startIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={onVoltar}
          >
            Voltar
          </AgnesButton>
        }
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <AgnesTabs value={tab} onValueChange={setTab} dataQa="horarios-detalhe-abas">
            <AgnesTabsList dataQa="horarios-detalhe-abas-lista">
              <AgnesTabsTrigger value="analise" dataQa="horarios-detalhe-aba-analise">
                Horários e Jornada
              </AgnesTabsTrigger>
              <AgnesTabsTrigger value="historico" dataQa="horarios-detalhe-aba-historico">
                Histórico
              </AgnesTabsTrigger>
              <AgnesTabsTrigger value="movimentacoes" dataQa="horarios-detalhe-aba-movimentacoes">
                Movimentações
              </AgnesTabsTrigger>
            </AgnesTabsList>

            <AgnesTabsContent value="analise" dataQa="horarios-detalhe-aba-analise-conteudo">
              <AgnesCard data-qa="horarios-detalhe-trabalhador">
                <AgnesCardHeader>
                  <AgnesCardTitle data-qa="horarios-detalhe-trabalhador-titulo">
                    Dados do trabalhador
                  </AgnesCardTitle>
                </AgnesCardHeader>
                <AgnesCardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Campo label="Nome do trabalhador" dataQa="horarios-detalhe-trabalhador-nome">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-nome"
                      value={nomeTrabalhador}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Cargo" dataQa="horarios-detalhe-trabalhador-cargo">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-cargo"
                      value={(detalhe.cargo as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Matrícula" dataQa="horarios-detalhe-trabalhador-matricula">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-matricula"
                      value={(detalhe.matricula as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="CPF" dataQa="horarios-detalhe-trabalhador-cpf">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-cpf"
                      value={UtilsMask.cpf(detalhe.cpf as string)}
                      readOnly
                    />
                  </Campo>
                  <Campo label="CNPJ" dataQa="horarios-detalhe-trabalhador-cnpj">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-cnpj"
                      value={UtilsMask.cnpj(detalhe.cnpj as string)}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Situação contratual"
                    dataQa="horarios-detalhe-trabalhador-situacao-contratual"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-situacao-contratual"
                      value={situacaoContratual}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Tipo de admissão"
                    dataQa="horarios-detalhe-trabalhador-tipo-admissao"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-tipo-admissao"
                      value={TIPO_ADMISSAO_LABEL[tipoAdmissao] ?? tipoAdmissao}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Início" dataQa="horarios-detalhe-trabalhador-inicio">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-inicio"
                      value={UtilsDate.formatBR(detalhe.dataAdmissao)}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Fim" dataQa="horarios-detalhe-trabalhador-fim">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-fim"
                      value={UtilsDate.formatBR(detalhe.dataDesligamento)}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Motivo de encerramento"
                    dataQa="horarios-detalhe-trabalhador-motivo-encerramento"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-motivo-encerramento"
                      value={(detalhe.motivoEncerramento as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Gênero" dataQa="horarios-detalhe-trabalhador-genero">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-genero"
                      value={GENERO_LABEL[genero] ?? genero}
                      readOnly
                    />
                  </Campo>
                  <Campo label="PCD" dataQa="horarios-detalhe-trabalhador-pcd">
                    <AgnesInput dataQa="horarios-detalhe-trabalhador-pcd" value={pcd} readOnly />
                  </Campo>
                  <Campo
                    label="Tipo de regime trabalhista"
                    dataQa="horarios-detalhe-trabalhador-regime-trabalhista"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-regime-trabalhista"
                      value={(detalhe.regimeTrabalhista as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Grupo de categoria"
                    dataQa="horarios-detalhe-trabalhador-grupo-categoria"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-grupo-categoria"
                      value={(detalhe.grupoCategoria as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Categoria" dataQa="horarios-detalhe-trabalhador-categoria">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-categoria"
                      value={(detalhe.categoria as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Regime previdenciário"
                    dataQa="horarios-detalhe-trabalhador-regime-previdenciario"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-regime-previdenciario"
                      value={(detalhe.regimePrevidenciario as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Empregador" dataQa="horarios-detalhe-trabalhador-empregador">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-empregador"
                      value={(detalhe.empresa as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo label="Ocupação" dataQa="horarios-detalhe-trabalhador-ocupacao">
                    <AgnesInput
                      dataQa="horarios-detalhe-trabalhador-ocupacao"
                      value={(detalhe.ocupacao as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                </AgnesCardContent>
              </AgnesCard>

              <AgnesCard className="mt-4">
                <AgnesCardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Campo
                    label="Regime de jornada (eSocial)"
                    dataQa="horarios-detalhe-regime-jornada-esocial"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-regime-jornada-esocial"
                      value={(detalhe.regimeJornada as string) ?? ''}
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Regime de jornada (Recomendação Agnes)"
                    dataQa="horarios-detalhe-regime-jornada-recomendacao"
                  >
                    <AgnesSelect
                      value={
                        form.workScheduleRegTypeCode != null
                          ? String(form.workScheduleRegTypeCode)
                          : ''
                      }
                      onValueChange={(v) => set('workScheduleRegTypeCode', v ? Number(v) : null)}
                      disabled={!analista}
                    >
                      <AgnesSelectTrigger dataQa="horarios-detalhe-regime-jornada-recomendacao">
                        <AgnesSelectValue placeholder="Regime" />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent dataQa="horarios-detalhe-regime-jornada-recomendacao-lista">
                        {optWorkRegime.map((o) => (
                          <AgnesSelectItem
                            dataQa="horarios-detalhe-regime-jornada-recomendacao"
                            key={o.code}
                            value={String(o.code)}
                          >
                            {o.description}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </Campo>

                  {/* Par: Tipo de jornada */}
                  <Campo
                    label="Tipo de jornada (eSocial)"
                    dataQa="horarios-detalhe-tipo-jornada-esocial"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-tipo-jornada-esocial"
                      value={(detalhe.tipoJornada as string) ?? ''}
                      readOnly
                    />
                  </Campo>

                  <Campo
                    label="Tipo de jornada (Recomendação Agnes)"
                    dataQa="horarios-detalhe-tipo-jornada-recomendacao"
                  >
                    <AgnesSelect
                      value={
                        form.workScheduleTypeCode != null ? String(form.workScheduleTypeCode) : ''
                      }
                      onValueChange={(v) => set('workScheduleTypeCode', v ? Number(v) : null)}
                      disabled={!analista}
                    >
                      <AgnesSelectTrigger dataQa="horarios-detalhe-tipo-jornada-recomendacao">
                        <AgnesSelectValue placeholder="Tipo" />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent dataQa="horarios-detalhe-tipo-jornada-recomendacao-lista">
                        {optJourneyType.map((o) => (
                          <AgnesSelectItem
                            dataQa="horarios-detalhe-tipo-jornada-recomendacao"
                            key={o.code}
                            value={String(o.code)}
                          >
                            {o.description}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </Campo>

                  {/* Par: Tempo parcial */}
                  <Campo
                    label="Tempo parcial (eSocial)"
                    dataQa="horarios-detalhe-tempo-parcial-esocial"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-tempo-parcial-esocial"
                      value={(detalhe.tempoParcial as string) ?? ''}
                      readOnly
                    />
                  </Campo>

                  <Campo
                    label="Tempo parcial (Recomendação Agnes)"
                    dataQa="horarios-detalhe-tempo-parcial-recomendacao"
                  >
                    <AgnesSelect
                      value={
                        form.partTimeContractCode != null ? String(form.partTimeContractCode) : ''
                      }
                      onValueChange={(v) => set('partTimeContractCode', v ? Number(v) : null)}
                      disabled={!analista}
                    >
                      <AgnesSelectTrigger dataQa="horarios-detalhe-tempo-parcial-recomendacao">
                        <AgnesSelectValue placeholder="Tempo parcial" />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent dataQa="horarios-detalhe-tempo-parcial-recomendacao-lista">
                        {optPartTime.map((o) => (
                          <AgnesSelectItem
                            dataQa="horarios-detalhe-tempo-parcial-recomendacao"
                            key={o.code}
                            value={String(o.code)}
                          >
                            {o.description}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </Campo>

                  {/* Par: Carga horária semanal */}
                  <Campo
                    label="Carga horária semanal (eSocial)"
                    dataQa="horarios-detalhe-carga-horaria-semanal-esocial"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-carga-horaria-semanal-esocial"
                      value={String(detalhe.cargaHorariaSemanal ?? '')}
                      readOnly
                    />
                  </Campo>

                  <Campo
                    label="Carga horária semanal (Recomendação Agnes)"
                    dataQa="horarios-detalhe-carga-horaria-semanal-recomendacao"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-carga-horaria-semanal-recomendacao"
                      type="number"
                      value={form.weeklyWorkHours ?? ''}
                      onChange={(e) =>
                        set(
                          'weeklyWorkHours',
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                      disabled={!analista}
                    />
                  </Campo>

                  {/* Par: Horário noturno */}
                  <Campo
                    label="Horário noturno (eSocial)"
                    dataQa="horarios-detalhe-horario-noturno-esocial"
                  >
                    <AgnesInput
                      dataQa="horarios-detalhe-horario-noturno-esocial"
                      value={
                        detalhe.horarioNoturno == null ? '' : detalhe.horarioNoturno ? 'Sim' : 'Não'
                      }
                      readOnly
                    />
                  </Campo>
                  <Campo
                    label="Horário noturno (Recomendação Agnes)"
                    dataQa="horarios-detalhe-horario-noturno-recomendacao"
                  >
                    <AgnesSelect
                      value={form.nightShift == null ? '' : String(form.nightShift)}
                      onValueChange={(v) => set('nightShift', v === '' ? null : v === 'true')}
                      disabled={!analista}
                    >
                      <AgnesSelectTrigger dataQa="horarios-detalhe-horario-noturno-recomendacao">
                        <AgnesSelectValue placeholder="Horário noturno" />
                      </AgnesSelectTrigger>
                      <AgnesSelectContent dataQa="horarios-detalhe-horario-noturno-recomendacao-lista">
                        {NOTURNO_OPTS.map((o) => (
                          <AgnesSelectItem
                            dataQa="horarios-detalhe-horario-noturno-recomendacao"
                            key={o.value}
                            value={o.value}
                          >
                            {o.label}
                          </AgnesSelectItem>
                        ))}
                      </AgnesSelectContent>
                    </AgnesSelect>
                  </Campo>

                  {/* Sem par correspondente: largura total (ponta a ponta) */}
                  <div className="md:col-span-2">
                    <Campo label="Etapa de análise" dataQa="horarios-detalhe-etapa-analise">
                      <AgnesSelect
                        value={form.step}
                        onValueChange={(v) => set('step', v)}
                        disabled={!analista}
                      >
                        <AgnesSelectTrigger dataQa="horarios-detalhe-etapa-analise">
                          <AgnesSelectValue placeholder="Etapa" />
                        </AgnesSelectTrigger>
                        <AgnesSelectContent dataQa="horarios-detalhe-etapa-analise-lista">
                          {Object.entries(STEP_LABEL).map(([v, l]) => (
                            <AgnesSelectItem
                              dataQa="horarios-detalhe-etapa-analise"
                              key={v}
                              value={v}
                            >
                              {l}
                            </AgnesSelectItem>
                          ))}
                        </AgnesSelectContent>
                      </AgnesSelect>
                    </Campo>
                  </div>
                  <div className="md:col-span-2">
                    <Campo
                      label="Ocorrência (Recomendação Agnes)"
                      dataQa="horarios-detalhe-ocorrencia-recomendacao"
                    >
                      <AgnesSelect
                        value={form.recommendationId != null ? String(form.recommendationId) : ''}
                        onValueChange={onOcorrencia}
                        disabled={!analista}
                      >
                        <AgnesSelectTrigger dataQa="horarios-detalhe-ocorrencia-recomendacao">
                          <AgnesSelectValue placeholder="Ocorrência" />
                        </AgnesSelectTrigger>
                        <AgnesSelectContent dataQa="horarios-detalhe-ocorrencia-recomendacao-lista">
                          {ocorrencias.map((o) => (
                            <AgnesSelectItem
                              dataQa="horarios-detalhe-ocorrencia-recomendacao"
                              key={o.id}
                              value={String(o.id)}
                            >
                              {o.occurrence}
                            </AgnesSelectItem>
                          ))}
                        </AgnesSelectContent>
                      </AgnesSelect>
                    </Campo>
                  </div>
                  <div className="md:col-span-2">
                    <Campo label="Grau de risco" dataQa="horarios-detalhe-grau-risco">
                      <AgnesInput
                        dataQa="horarios-detalhe-grau-risco"
                        value={risco ? (RISK_LABEL[risco] ?? risco) : ''}
                        readOnly
                      />
                    </Campo>
                  </div>
                  <div className="md:col-span-2">
                    <Campo
                      label="Classificação (prévia)"
                      dataQa="horarios-detalhe-classificacao-previa"
                    >
                      <AgnesInput
                        dataQa="horarios-detalhe-classificacao-previa"
                        value={CLASSIFICATION_LABEL[classificacaoPreview] ?? classificacaoPreview}
                        readOnly
                      />
                    </Campo>
                  </div>
                  {/* Recomendações adicionais — campo livre, largura total */}
                  <div className="md:col-span-2">
                    <Campo
                      label="Recomendações adicionais"
                      dataQa="horarios-detalhe-recomendacoes-adicionais"
                    >
                      <AgnesInput
                        dataQa="horarios-detalhe-recomendacoes-adicionais"
                        value={form.comentarios}
                        onChange={(e) => set('comentarios', e.target.value)}
                        disabled={!analista}
                      />
                    </Campo>
                  </div>
                </AgnesCardContent>
              </AgnesCard>
            </AgnesTabsContent>

            <AgnesTabsContent value="historico" dataQa="horarios-detalhe-aba-historico-conteudo">
              <TableWrapper>
                <AgnesDataTable<HistoricoMovimentacao>
                  dataQa="horarios-detalhe-historico-tabela"
                  columns={comQaNasCelulas('horarios-detalhe-historico-tabela', histCols, (r) =>
                    String(r.id ?? `${r.changedField}-${r.updatedAt}`),
                  )}
                  data={historico}
                  rowKey={(r) => String(r.id ?? `${r.changedField}-${r.updatedAt}`)}
                  pagination={{
                    page: 0,
                    pageSize: 10,
                    total: historico.length,
                    onPageChange: () => {},
                    onPageSizeChange: () => {},
                  }}
                />
              </TableWrapper>
            </AgnesTabsContent>

            <AgnesTabsContent
              value="movimentacoes"
              dataQa="horarios-detalhe-aba-movimentacoes-conteudo"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  <AgnesSelect
                    value=""
                    onValueChange={(v) => setEventosFiltro(idTrab, v ? { version: [v] } : {})}
                  >
                    <AgnesSelectTrigger
                      className="w-56"
                      dataQa="horarios-detalhe-movimentacoes-versao"
                    >
                      <AgnesSelectValue placeholder="Versão do eSocial" />
                    </AgnesSelectTrigger>
                    <AgnesSelectContent dataQa="horarios-detalhe-movimentacoes-versao-lista">
                      {eventosFiltros.versions.map((v) => (
                        <AgnesSelectItem
                          dataQa="horarios-detalhe-movimentacoes-versao"
                          key={v}
                          value={v}
                        >
                          {v}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>
                  <AgnesSelect
                    value=""
                    onValueChange={(v) => setEventosFiltro(idTrab, v ? { eventCode: [v] } : {})}
                  >
                    <AgnesSelectTrigger
                      className="w-64"
                      dataQa="horarios-detalhe-movimentacoes-evento"
                    >
                      <AgnesSelectValue placeholder="Evento" />
                    </AgnesSelectTrigger>
                    <AgnesSelectContent dataQa="horarios-detalhe-movimentacoes-evento-lista">
                      {eventosFiltros.events.map((e) => (
                        <AgnesSelectItem
                          dataQa="horarios-detalhe-movimentacoes-evento"
                          key={e.code}
                          value={e.code}
                        >
                          {e.description ?? e.code}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>
                </div>
                <ExportButton
                  dataQa="horarios-detalhe-movimentacoes-exportar"
                  variant="outlined"
                  startIcon={<Download className="h-4 w-4" />}
                  onClick={() => exportarEventos(idTrab)}
                >
                  Exportar
                </ExportButton>
              </div>
              <TableWrapper>
                <AgnesDataTable<Record<string, unknown>>
                  dataQa="horarios-detalhe-movimentacoes-tabela"
                  columns={comQaNasCelulas(
                    'horarios-detalhe-movimentacoes-tabela',
                    MOVIMENTACOES_COLUMNS,
                    (r) => String(r.eventoId),
                  )}
                  data={eventos}
                  rowKey={(r) => String(r.eventoId)}
                  pagination={{
                    page: eventosPage,
                    pageSize: 50,
                    total: eventosTotal,
                    onPageChange: (p: number) => setEventosPage(idTrab, p),
                    onPageSizeChange: () => {},
                  }}
                />
              </TableWrapper>
            </AgnesTabsContent>
          </AgnesTabs>
        </AgnesCardContent>
      </AgnesCard>

      {analista && tab === 'analise' && (
        <div data-qa="horarios-detalhe-acoes" className="mt-4 flex justify-start">
          <AgnesButton
            variant="primary"
            data-qa="horarios-detalhe-salvar"
            startIcon={<Save className="h-4 w-4" />}
            disabled={salvando || !isDirty}
            onClick={onSalvar}
          >
            Salvar
          </AgnesButton>
        </div>
      )}
    </>
  );
}
